import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ChatMessage } from '@wundo/shared/chat.ts';
import type { RoleplayEvent } from '@wundo/shared/roleplay.ts';
import { ChatComposer, ChatSurface, ChatSurfaceFooter } from '@wundo/ui';
import { ChatBody } from '@/components/ChatBody.tsx';
import { TYPING_TIMEOUT_MS } from '@/lib/chatTyping.ts';
import { useRoleplayRoom } from '@/lib/roleplayRoom.ts';
import {
  fetchRoleplayMessages,
  type RoleplayMessagePage,
  sendRoleplayMessage,
} from '@/lib/roleplaySession.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeChatProps {
  sessionId: string;
  personaName: string;
  /** The character has signalled the conversation has run its course. */
  onSuggestEnd: () => void;
}

/**
 * A reply as it is being written: the message it is going to be, and what has
 * arrived of it so far. It is replaced by that message the moment it lands,
 * which is the only thing that says the reply is finished.
 */
interface StreamingReply {
  id: string;
  text: string;
}

/**
 * A written practice conversation.
 *
 * The same conversation the chat drawer holds, with somebody else on the other
 * end: the message goes out as a request, everything the character says comes
 * back on the session's room, and it is read with the drawer's own machinery —
 * so a reply unrolls as it is written, an older page loads when the reader goes
 * back for it, and nobody is yanked out of the part they were reading.
 *
 * The character always speaks first, so the conversation is never empty for
 * long — and until it says something there is a line saying so, rather than a
 * blank panel that reads as a page that failed.
 */
export function PracticeChat({
  sessionId,
  personaName,
  onSuggestEnd,
}: PracticeChatProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /*
   * The conversation is read backwards a page at a time: the most recent page
   * first, because that is the one being carried on, and older ones only if the
   * reader goes looking for them.
   */
  const {
    data: pages,
    isError: historyFailed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['roleplayMessages', sessionId],
    queryFn: async ({ pageParam }) => await fetchRoleplayMessages(sessionId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (page: RoleplayMessagePage) => page.older,
  });

  /* Pages run newest to oldest; the conversation reads the other way. */
  const history = useMemo(
    () => [...(pages?.pages ?? [])].reverse().flatMap((page) => page.messages),
    [pages],
  );

  const [live, setLive] = useState<readonly ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<readonly StreamingReply[]>([]);
  const [answering, setAnswering] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  /*
   * Held in a ref so the socket is not torn down and rebuilt every time the
   * screen around it renders — which would lose the conversation and ask for it
   * again on every keystroke.
   */
  const suggestEnd = useRef(onSuggestEnd);
  useEffect(() => {
    suggestEnd.current = onSuggestEnd;
  });

  /*
   * What is read back and what arrives live are joined at the end, and the same
   * message is only kept once: the user's own message is answered by the send
   * and echoed on the room under the same id.
   */
  const messages = useMemo(() => {
    const seen = new Set<string>();

    return [...history, ...live].filter((message) => {
      if (seen.has(message.id)) {
        return false;
      }
      seen.add(message.id);
      return true;
    });
  }, [history, live]);

  /*
   * Minus anything already here in full: the finished message carries the id
   * its pieces did, so the two would otherwise show as two for as long as one
   * render lasts.
   */
  const streamingReplies = useMemo(
    () =>
      streaming.filter((reply) => !messages.some((message) => message.id === reply.id)),
    [streaming, messages],
  );

  /*
   * Dots are for a reply nobody can see yet. Once its words are arriving they
   * say it better than the dots do.
   */
  const typing = answering && streamingReplies.length === 0;
  /*
   * One turn speaks at a time: while a message is on its way out or a reply is
   * on its way in, the composer is held.
   */
  const responding = answering || streamingReplies.length > 0;

  const append = useCallback((message: ChatMessage) => {
    setLive((current) =>
      current.some((one) => one.id === message.id) ? current : [...current, message],
    );
  }, []);

  const onEvent = useCallback(
    (event: RoleplayEvent) => {
      if (event.type === 'suggest_end') {
        suggestEnd.current();
        return;
      }

      if (event.type === 'typing') {
        setAnswering(true);
        return;
      }

      /* A practice partner is somebody being played, not Wundo at work. */
      if (event.type === 'activity') {
        return;
      }

      /*
       * A piece is only ever the next bit of the message it names, so a piece for
       * another message begins one — under whatever was written before it.
       */
      if (event.type === 'delta') {
        setStreaming((current) => {
          const writing = current.at(-1);

          return writing && writing.id === event.message_id
            ? [
                ...current.slice(0, -1),
                { id: writing.id, text: writing.text + event.text },
              ]
            : [...current, { id: event.message_id, text: event.text }];
        });
        return;
      }

      setAnswering(false);
      /*
       * This message is here in full now, and so is everything written before it.
       * What comes after is still being written, and stays.
       */
      setStreaming((current) => {
        const finished = current.findIndex((reply) => reply.id === event.message.id);
        return finished === -1 ? [] : current.slice(finished + 1);
      });
      append(event.message);
    },
    [append],
  );

  /*
   * The line was down, so anything said over it was missed. Reading the
   * conversation back is the only way to find out what — and what arrived live
   * is dropped rather than kept alongside it, because a message pushed before
   * it was stored does not carry the id it will be read back under.
   */
  const onReconnect = useCallback(() => {
    setLive([]);
    setStreaming([]);
    setAnswering(false);

    void queryClient.invalidateQueries({
      queryKey: ['roleplayMessages', sessionId],
    });
  }, [queryClient, sessionId]);

  useRoleplayRoom(sessionId, onEvent, onReconnect);

  /*
   * A reply that never comes — the turn failed, or the socket missed it —
   * would otherwise leave the dots bouncing for the rest of the conversation.
   */
  useEffect(() => {
    if (!answering) {
      return;
    }

    const timer = setTimeout(() => setAnswering(false), TYPING_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [answering]);

  const send = useMutation({
    mutationFn: async (text: string) => await sendRoleplayMessage(sessionId, text),
    onSuccess: (message) => {
      setSendFailed(false);
      setAnswering(true);
      append(message);
    },
    onError: () => {
      setSendFailed(true);
      setAnswering(false);
    },
  });

  const onSend = useCallback((text: string) => send.mutate(text), [send]);
  const loadOlder = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  return (
    /*
     * The card takes the height it is given rather than the height its content
     * asks for: the conversation scrolls inside it and the composer stays where
     * the hand is, where an unbounded card grows with every turn and walks the
     * composer off the bottom of the page.
     */
    <ChatSurface className='min-h-95 flex-1'>
      <ChatBody
        messages={messages}
        streaming={streamingReplies}
        typing={typing}
        typingLabel={m.practice_session_chat_typing({ name: personaName })}
        authorLabels={{ user: m.practice_session_you(), wundo: personaName }}
        empty={
          <p className='text-base text-ink-muted'>
            {m.practice_session_chat_starting({ name: personaName })}
          </p>
        }
        /* The same two things that can go wrong in any conversation. */
        error={
          sendFailed ? m.chat_send_failed() : historyFailed ? m.chat_load_failed() : null
        }
        onSuggestion={onSend}
        sending={send.isPending}
        hasOlder={hasNextPage}
        loadingOlder={isFetchingNextPage}
        onLoadOlder={loadOlder}
        /* Same as anywhere else: one of our own pages is reached in this tab. */
        onNavigate={(path) => void navigate({ href: path })}
      />

      <ChatSurfaceFooter>
        <ChatComposer
          placeholder={m.practice_session_chat_placeholder()}
          sendLabel={m.practice_session_chat_send()}
          focusOn={sessionId}
          disabled={send.isPending || responding}
          onSend={onSend}
        />
      </ChatSurfaceFooter>
    </ChatSurface>
  );
}
